import React, { useEffect, useRef, useState } from "react";
import { api, formatDate, formatEUR, hasPerm } from "@/lib/api";
import { toast } from "sonner";
import { Package, ArrowDownToLine, ArrowUpFromLine, ArrowRightLeft, AlertTriangle, X, Download, Upload, Plus, Minus, Barcode, Pencil } from "lucide-react";
import { downloadFile } from "@/lib/download";
import { useStore } from "@/lib/storeContext";

const KIND_LABEL = { load: "Carico", unload: "Scarico", transfer: "Trasferimento", adjust: "Rettifica" };
const KIND_ICON = { load: ArrowDownToLine, unload: ArrowUpFromLine, transfer: ArrowRightLeft, adjust: Package };

export default function Warehouse() {
  const { storeId: ctxStore, setStoreId: setCtxStore, multi } = useStore();
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeStore, setActiveStore] = useState(ctxStore || "");
  const [showMove, setShowMove] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [cats, setCats] = useState([]);
  const [query, setQuery] = useState("");
  const fileRef = useRef(null);
  const scanRef = useRef(null);
  const seePurchase = hasPerm("can_view_purchase_prices");

  const exportExcel = async () => {
    try { await downloadFile("/warehouse/export-excel", "magazzino_velta.xlsx"); toast.success("Export avviato"); }
    catch { toast.error("Errore export"); }
  };

  const downloadTemplate = async () => {
    try { await downloadFile("/warehouse/export-excel?template=1", "template_magazzino_velta.xlsx"); toast.success("Template scaricato"); }
    catch { toast.error("Errore download"); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/warehouse/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${r.data.updated} scorte aggiornate${r.data.skipped_count ? ` · ${r.data.skipped_count} righe ignorate (articolo non trovato)` : ""}`, { duration: 7000 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore import"); }
    e.target.value = "";
  };

  const load = () => {
    api.get("/stores").then(r => setStores(Array.isArray(r.data) ? r.data : []));
    api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => setCats([]));
    api.get("/products").then(r => setProducts(Array.isArray(r.data) ? r.data : []));
    api.get(`/warehouse/stocks${activeStore ? `?store_id=${activeStore}` : ""}`).then(r => setStocks(Array.isArray(r.data) ? r.data : []));
    api.get(`/warehouse/movements?limit=50${activeStore ? `&store_id=${activeStore}` : ""}`).then(r => setMovements(Array.isArray(r.data) ? r.data : []));
    api.get("/warehouse/low-stock").then(r => setAlerts(Array.isArray(r.data) ? r.data : []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeStore]);
  useEffect(() => { if (ctxStore !== undefined && ctxStore !== activeStore && multi) setActiveStore(ctxStore); }, [ctxStore]); // eslint-disable-line

  const pickStore = (id) => {
    setActiveStore(id);
    if (multi) setCtxStore(id);
  };

  const quickMove = async (s, kind) => {
    const dest = s.store_id || activeStore || stores[0]?.id;
    if (!dest) return toast.error("Seleziona un punto vendita");
    try {
      await api.post("/warehouse/movements", {
        kind,
        product_id: s.product_id,
        qty: 1,
        to_store_id: kind === "load" ? dest : null,
        from_store_id: kind === "unload" ? dest : null,
      });
      toast.success(kind === "load" ? "Carico +1" : "Scarico −1");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Movimento non riuscito"); }
  };

  const onScan = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = (e.currentTarget.value || "").trim();
    if (!code) return;
    try {
      const r = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
      setQuery(r.data.name || code);
      e.currentTarget.value = "";
      toast.success(`Trovato: ${r.data.name}`);
      const p = r.data;
      setEditRow({
        product_id: p.id,
        product_name: p.name,
        barcode: p.barcode,
        price: p.price,
        purchase_price: p.purchase_price,
        stock_limit: p.stock_limit,
        category_id: p.category_id,
      });
    } catch {
      toast.error("Nessun articolo con questo codice — crealo con «Nuovo articolo»");
    }
  };

  const stockByPid = {};
  (Array.isArray(stocks) ? stocks : []).forEach((s) => {
    if (!stockByPid[s.product_id]) stockByPid[s.product_id] = { ...s };
    else stockByPid[s.product_id].qty = (Number(stockByPid[s.product_id].qty) || 0) + (Number(s.qty) || 0);
  });
  const qn = query.trim().toLowerCase();
  const rows = (Array.isArray(products) ? products : [])
    .filter((p) => p && p.visible !== false)
    .map((p) => {
      const s = stockByPid[p.id];
      const qty = s ? s.qty : (p.stock_current ?? 0);
      return {
        product_id: p.id,
        product_name: p.name,
        qty,
        stock_limit: p.stock_limit,
        store_id: s?.store_id || activeStore || stores[0]?.id,
        low: p.stock_limit != null && qty <= p.stock_limit,
        barcode: p.barcode,
        price: p.price,
        purchase_price: p.purchase_price,
        category_id: p.category_id,
      };
    })
    .filter((r) => {
      if (!qn) return true;
      return (r.product_name || "").toLowerCase().includes(qn) || String(r.barcode || "").toLowerCase().includes(qn);
    });

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Magazzino</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Scorte & Movimenti</h1>
          <p className="text-velta-mute mt-2 max-w-xl text-sm">Stessi articoli della cassa. Vendita = scarico automatico. Soglia minima: avviso in cassa, si può togliere quando vuoi.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />
          <button data-testid="wh-import-btn" onClick={() => fileRef.current?.click()} className="h-12 px-4 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Upload className="w-4 h-4" /> Importa Excel
          </button>
          <button data-testid="wh-export-btn" onClick={exportExcel} className="h-12 px-4 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Download className="w-4 h-4" /> Esporta Excel
          </button>
          <button data-testid="wh-template-btn" onClick={downloadTemplate} title="Scarica un Excel vuoto con le sole intestazioni, pronto da compilare" className="h-12 px-4 bg-white border border-dashed border-velta-ink text-velta-ink hover:bg-velta-bg rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Download className="w-4 h-4" /> Template vuoto
          </button>
          <button data-testid="warehouse-new-item-btn" onClick={() => setShowNew(true)} className="h-12 px-5 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Plus className="w-4 h-4" /> Nuovo articolo
          </button>
          <button data-testid="warehouse-new-move-btn" onClick={() => setShowMove(true)} className="h-12 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Package className="w-4 h-4" /> Nuovo movimento
          </button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-yellow-900 mb-2">Scorte sotto soglia ({alerts.length})</div>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(alerts) ? alerts : []).map(a => (
                  <span key={a.product_id} className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-yellow-300 rounded-md text-xs">
                    <span className="font-medium">{a.product_name}</span>
                    <span className="font-mono text-yellow-700">{a.qty} / {a.limit}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-velta-border rounded-md p-5 mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Filtra per punto vendita</div>
        <div className="flex gap-2 flex-wrap mb-4">
          <button onClick={() => pickStore("")} className={`h-10 px-4 rounded-md text-sm font-medium btn-tactile ${activeStore === "" ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}>Tutti</button>
          {(Array.isArray(stores) ? stores : []).map(s => (
            <button key={s.id} data-testid={`warehouse-store-${s.id}`} onClick={() => pickStore(s.id)} className={`h-10 px-4 rounded-md text-sm font-medium btn-tactile ${activeStore === s.id ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}>{s.name}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Cerca</span>
            <input data-testid="wh-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nome o codice…" className="h-11 w-full border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </label>
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2 flex items-center gap-1.5"><Barcode className="w-3.5 h-3.5" /> Lettore barcode</span>
            <input ref={scanRef} data-testid="wh-barcode-scan" onKeyDown={onScan} placeholder="Punta la pistola qui e Invio…" className="h-11 w-full border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          </label>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-velta-border rounded-md overflow-hidden">
          <div className="px-5 py-4 border-b border-velta-border">
            <h3 className="font-heading text-lg font-semibold">Scorte attuali</h3>
          </div>
          <div className="max-h-[500px] overflow-auto pos-scroll">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-velta-bg sticky top-0">
                <tr className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
                  <th className="px-5 py-3">Articolo</th>
                  <th className="px-5 py-3">Barcode</th>
                  <th className="px-5 py-3 text-right">Prezzo vendita</th>
                  {seePurchase && <th className="px-5 py-3 text-right">Prezzo acquisto</th>}
                  <th className="px-5 py-3 text-right">Qta</th>
                  <th className="px-5 py-3 text-right">Soglia</th>
                  <th className="px-3 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(rows) ? rows : []).map((s) => (
                    <tr key={s.product_id} className={`border-b border-velta-border last:border-0 ${s.low ? "bg-yellow-50" : ""}`}>
                      <td className="px-5 py-3 font-medium">{s.product_name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-velta-mute">{s.barcode || "—"}</td>
                      <td className="px-5 py-3 text-right font-mono" data-testid={`wh-price-${s.product_id}`}>{s.price != null ? formatEUR(s.price) : "—"}</td>
                      {seePurchase && (
                        <td className="px-5 py-3 text-right font-mono text-velta-mute" data-testid={`wh-purchase-${s.product_id}`}>{s.purchase_price != null ? formatEUR(s.purchase_price) : "—"}</td>
                      )}
                      <td className="px-5 py-3 text-right font-mono font-bold">{s.qty}</td>
                      <td className="px-5 py-3 text-right font-mono text-velta-mute text-xs">{s.stock_limit ?? "—"}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button type="button" data-testid={`wh-edit-${s.product_id}`} onClick={() => setEditRow(s)} className="h-8 w-8 inline-flex items-center justify-center rounded border border-velta-border hover:bg-velta-bg" title="Modifica prezzi e soglia"><Pencil className="w-3.5 h-3.5" /></button>
                        <button type="button" data-testid={`wh-quick-unload-${s.product_id}`} onClick={() => quickMove(s, "unload")} className="h-8 w-8 inline-flex items-center justify-center rounded border border-velta-border hover:bg-red-50 ml-1" title="Scarico 1"><Minus className="w-3.5 h-3.5" /></button>
                        <button type="button" data-testid={`wh-quick-load-${s.product_id}`} onClick={() => quickMove(s, "load")} className="h-8 w-8 inline-flex items-center justify-center rounded border border-velta-border hover:bg-emerald-50 ml-1" title="Carico 1"><Plus className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={seePurchase ? 7 : 6} className="px-5 py-12 text-center text-velta-mute">Nessun articolo. Creane uno o scegli una categoria in Articoli.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-velta-border rounded-md overflow-hidden">
          <div className="px-5 py-4 border-b border-velta-border">
            <h3 className="font-heading text-lg font-semibold">Ultimi movimenti</h3>
          </div>
          <div className="max-h-[500px] overflow-auto pos-scroll">
            {(Array.isArray(movements) ? movements : []).map(m => {
              const Icon = KIND_ICON[m.kind] || Package;
              const prod = (Array.isArray(products) ? products : []).find(p => p.id === m.product_id);
              return (
                <div key={m.id} className="px-5 py-3 border-b border-velta-border last:border-0 flex items-center gap-4">
                  <Icon className="w-5 h-5 text-velta-subtle shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{prod?.name || "Articolo"}</div>
                    <div className="text-xs text-velta-mute font-mono">{KIND_LABEL[m.kind]} · {formatDate(m.created_at)}{m.supplier ? ` · ${m.supplier}` : ""}</div>
                  </div>
                  <div className={`font-mono font-bold ${m.kind === "unload" ? "text-velta-error" : "text-velta-success"}`}>
                    {m.kind === "unload" ? "−" : "+"}{m.qty}
                  </div>
                </div>
              );
            })}
            {movements.length === 0 && <div className="p-12 text-center text-velta-mute">Nessun movimento registrato.</div>}
          </div>
        </div>
      </div>

      {showMove && <MovementModal stores={stores} products={products} defaultStore={activeStore} seePurchase={seePurchase} onClose={() => setShowMove(false)} onSaved={() => { setShowMove(false); load(); toast.success("Movimento registrato"); }} />}
      {showNew && <NewWarehouseItemModal cats={cats} stores={stores} defaultStore={activeStore} seePurchase={seePurchase} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); toast.success("Articolo creato e visibile in cassa"); }} />}
      {editRow && <EditWarehouseItemModal row={editRow} cats={cats} seePurchase={seePurchase} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); toast.success("Articolo aggiornato"); }} />}
    </div>
  );
}

function MovementModal({ stores, products, defaultStore, seePurchase, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "load", from_store_id: "", to_store_id: defaultStore || stores[0]?.id || "", product_id: products[0]?.id || "", qty: 1, unit_cost: "", reason: "", document_ref: "", supplier: "" });

  const submit = async () => {
    try {
      const payload = { ...f, qty: parseFloat(f.qty) || 0, unit_cost: f.unit_cost ? parseFloat(f.unit_cost) : null };
      if (f.kind === "load") payload.from_store_id = null;
      if (f.kind === "unload") payload.to_store_id = null;
      await api.post("/warehouse/movements", payload);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">Movimento magazzino</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Tipo</label>
            <div className="grid grid-cols-4 gap-1">
              {["load", "unload", "transfer", "adjust"].map(k => (
                <button key={k} data-testid={`move-kind-${k}`} onClick={() => setF({ ...f, kind: k })} className={`h-11 rounded-md text-xs font-medium btn-tactile ${f.kind === k ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink"}`}>{KIND_LABEL[k]}</button>
              ))}
            </div>
          </div>
          {(f.kind === "unload" || f.kind === "transfer") && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Da magazzino</label>
              <select value={f.from_store_id} onChange={(e) => setF({ ...f, from_store_id: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
                <option value="">— seleziona —</option>
                {(Array.isArray(stores) ? stores : []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {(f.kind === "load" || f.kind === "transfer" || f.kind === "adjust") && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">A magazzino</label>
              <select value={f.to_store_id} onChange={(e) => setF({ ...f, to_store_id: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
                <option value="">— seleziona —</option>
                {(Array.isArray(stores) ? stores : []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Articolo</label>
            <input
              data-testid="move-barcode-scan"
              placeholder="Scansiona codice a barre… (Invio)"
              onKeyDown={async (e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                const input = e.currentTarget;
                const code = input.value.trim();
                if (!code) return;
                try {
                  const r = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
                  setF((prev) => ({ ...prev, product_id: r.data.id }));
                  toast.success(`Articolo: ${r.data.name}`);
                  input.value = "";
                } catch { toast.error("Nessun prodotto con questo codice"); }
              }}
              className="h-11 w-full mb-2 bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
            />
            <select data-testid="move-product" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
              {(Array.isArray(products) ? products : []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Quantità</label>
              <input data-testid="move-qty" type="number" step="0.01" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            </div>
            {seePurchase && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Costo unit. €</label>
              <input type="number" step="0.01" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" placeholder="opz." />
            </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Fornitore</label>
            <input data-testid="move-supplier" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" placeholder="Nome fornitore (carichi)" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Riferimento documento</label>
            <input value={f.document_ref} onChange={(e) => setF({ ...f, document_ref: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" placeholder="DDT n., fattura fornitore…" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Note</label>
            <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
        </div>
        <button data-testid="move-save-btn" onClick={submit} className="w-full mt-6 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Registra movimento</button>
      </div>
    </div>
  );
}

function NewWarehouseItemModal({ cats, stores, defaultStore, seePurchase, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "",
    category_id: cats[0]?.id || "",
    price: "",
    purchase_price: "",
    barcode: "",
    stock_limit: "",
    qty: "0",
    store_id: defaultStore || stores[0]?.id || "",
    vat: 22,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category_id) return toast.error("Scegli la categoria della cassa");
    setBusy(true);
    try {
      const limit = f.stock_limit === "" ? null : parseInt(f.stock_limit, 10);
      await api.post("/warehouse/items", {
        name: f.name.trim(),
        category_id: f.category_id,
        price: parseFloat(String(f.price).replace(",", ".")) || 0,
        vat: parseFloat(f.vat) || 22,
        barcode: f.barcode.trim() || null,
        purchase_price: seePurchase && f.purchase_price !== "" ? parseFloat(String(f.purchase_price).replace(",", ".")) : null,
        stock_limit: Number.isFinite(limit) ? limit : null,
        qty: parseFloat(String(f.qty).replace(",", ".")) || 0,
        store_id: f.store_id || null,
        visible: true,
      });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form data-testid="wh-new-item-form" onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">Nuovo articolo magazzino</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-velta-mute mb-4">Compare subito nella categoria scelta in cassa. Pistola barcode nel campo codice.</p>
        <div className="space-y-3">
          <input required data-testid="wh-new-name" placeholder="Nome articolo" value={f.name} onChange={set("name")} className="h-11 w-full border border-velta-border rounded-md px-3" />
          <select required data-testid="wh-new-cat" value={f.category_id} onChange={set("category_id")} className="h-11 w-full border border-velta-border rounded-md px-3">
            <option value="">— Categoria cassa —</option>
            {(Array.isArray(cats) ? cats : []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.mode ? ` (${c.mode})` : ""}</option>)}
          </select>
          <input data-testid="wh-new-barcode" placeholder="Codice a barre (scansiona qui)" value={f.barcode} onChange={set("barcode")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
          <div className="grid grid-cols-2 gap-3">
            <input required data-testid="wh-new-price" type="number" step="0.01" min="0" placeholder="Prezzo vendita €" value={f.price} onChange={set("price")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
            {seePurchase && (
              <input data-testid="wh-new-purchase" type="number" step="0.01" min="0" placeholder="Prezzo acquisto €" value={f.purchase_price} onChange={set("purchase_price")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="wh-new-qty" type="number" step="0.01" min="0" placeholder="Scorta iniziale" value={f.qty} onChange={set("qty")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
            <input data-testid="wh-new-limit" type="number" min="0" placeholder="Soglia min (vuoto = no)" value={f.stock_limit} onChange={set("stock_limit")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
          </div>
          {(Array.isArray(stores) ? stores : []).length > 1 && (
            <select value={f.store_id} onChange={set("store_id")} className="h-11 w-full border border-velta-border rounded-md px-3">
              {(Array.isArray(stores) ? stores : []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
        <button type="submit" disabled={busy} data-testid="wh-new-save" className="w-full mt-6 h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile disabled:opacity-50">{busy ? "Salvataggio…" : "Crea e metti in cassa"}</button>
      </form>
    </div>
  );
}

function EditWarehouseItemModal({ row, cats, seePurchase, onClose, onSaved }) {
  const [f, setF] = useState({
    name: row.product_name || "",
    category_id: row.category_id || "",
    price: row.price ?? "",
    purchase_price: row.purchase_price ?? "",
    barcode: row.barcode || "",
    stock_limit: row.stock_limit ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const emptyLimit = f.stock_limit === "";
      await api.patch(`/warehouse/items/${row.product_id}`, {
        name: f.name.trim(),
        category_id: f.category_id || undefined,
        price: parseFloat(String(f.price).replace(",", ".")) || 0,
        barcode: f.barcode.trim() || null,
        purchase_price: seePurchase && f.purchase_price !== "" ? parseFloat(String(f.purchase_price).replace(",", ".")) : undefined,
        stock_limit: emptyLimit ? undefined : parseInt(f.stock_limit, 10),
        clear_stock_limit: emptyLimit,
      });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form data-testid="wh-edit-item-form" onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold">Modifica articolo</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input required value={f.name} onChange={set("name")} className="h-11 w-full border border-velta-border rounded-md px-3" />
          <select value={f.category_id} onChange={set("category_id")} className="h-11 w-full border border-velta-border rounded-md px-3">
            {(Array.isArray(cats) ? cats : []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input data-testid="wh-edit-barcode" placeholder="Codice a barre" value={f.barcode} onChange={set("barcode")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-velta-mute">Prezzo vendita €</label>
              <input required data-testid="wh-edit-price" type="number" step="0.01" value={f.price} onChange={set("price")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
            </div>
            {seePurchase && (
              <div>
                <label className="text-[10px] font-mono uppercase text-velta-mute">Prezzo acquisto €</label>
                <input data-testid="wh-edit-purchase" type="number" step="0.01" value={f.purchase_price} onChange={set("purchase_price")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-velta-mute">Soglia minima (vuoto = nessun avviso)</label>
            <input data-testid="wh-edit-limit" type="number" min="0" value={f.stock_limit} onChange={set("stock_limit")} className="h-11 w-full border border-velta-border rounded-md px-3 font-mono" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="wh-edit-save" className="w-full mt-6 h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile disabled:opacity-50">{busy ? "Salvataggio…" : "Salva"}</button>
      </form>
    </div>
  );
}
